import PointerInteraction from 'ol/interaction/Pointer';
import { getLogger } from 'cadenza/utils/logging';

import {
  RoutingEvent,
  RoutingFeatureProperty,
  RoutingFeatureType
} from '../routing/routing-constants';

const LOG = getLogger('cadenza/routing/interaction');

export class RoutingInteraction extends PointerInteraction {

  #map;
  #routingEventBus;
  #routingFeatureLayer;

  #currentHoverWaypoint;
  #currentDraggedWaypoint;
  #dragStartCoordinates;

  constructor (map, routingEventBus, routingFeatureLayer) {
    super({ handleEvent: event => this._handleMapEvent(event) });

    this.#map = map;
    this.#routingEventBus = routingEventBus;
    this.#routingFeatureLayer = routingFeatureLayer;
  }

  _handleMapEvent (mapBrowserEvent) {
    let stopPropagation = false;

    switch (mapBrowserEvent.type) {
      case 'pointermove':
        this._handlePointerMove(mapBrowserEvent);
        break;
      case 'click':
        this._handleClick(mapBrowserEvent);
        break;
      case 'pointerdown':
        this._handlePointerDown();
        break;
      case 'pointerup':
        this._handlePointerUp(mapBrowserEvent);
        break;
      case 'pointerdrag':
        stopPropagation = this._handlePointerDrag(mapBrowserEvent);
        break;
      default:
        LOG.trace('RoutingInteraction: Unhandled event:', mapBrowserEvent);
    }

    return !stopPropagation;
  }

  _handlePointerMove (pointerMoveEvent) {
    this.#routingFeatureLayer.layer.getFeatures(pointerMoveEvent.pixel)
      .then(foundFeature => {
        const feature = foundFeature.length === 0 ? null : foundFeature[0];
        const featureIsWaypoint = feature && feature.get(RoutingFeatureProperty.TYPE) === RoutingFeatureType.WAYPOINT;

        const currentHoverWaypointIsAnotherWaypoint = this.#currentHoverWaypoint && featureIsWaypoint && feature !== this.#currentHoverWaypoint;

        if (!featureIsWaypoint && this.#currentHoverWaypoint || currentHoverWaypointIsAnotherWaypoint) {
          this._onPointerExitWaypoint(this.#currentHoverWaypoint);
        }

        if (featureIsWaypoint && feature !== this.#currentHoverWaypoint) {
          this._onPointerEnterWaypoint(feature);
        }
      })
      .catch(error => LOG.error('Error detecting feature under pointer', error));
  }

  _handleClick (clickEvent) {
    if (this.#currentHoverWaypoint) {
      this._onClickOnWaypoint(this.#currentHoverWaypoint);
    } else {
      this._onClickOnMap(clickEvent.coordinate);
    }
  }

  _handlePointerDown () {
    if (this.#currentHoverWaypoint) {
      this.#dragStartCoordinates = this.#currentHoverWaypoint.getGeometry().getCoordinates();
      this.#currentDraggedWaypoint = this.#currentHoverWaypoint;
    }
  }

  _handlePointerUp (pointerUpEvent) {
    if (!this.#currentDraggedWaypoint) {
      return;
    }
    this.#routingEventBus.dispatchEvent(
      new CustomEvent(RoutingEvent.WAYPOINT_POINTER_UP,
        {
          detail: {
            waypointId: this.#currentDraggedWaypoint.getId(),
            newCoordinate: pointerUpEvent.coordinate,
            dragStartCoordinate: this.#dragStartCoordinates,
            waypoint: this.#currentDraggedWaypoint
          }
        }));
    this.#dragStartCoordinates = null;
    this.#currentDraggedWaypoint = null;
  }

  _handlePointerDrag (dragEvent) {
    if (!this.#currentDraggedWaypoint) {
      return false;
    }

    this.#routingEventBus.dispatchEvent(
      new CustomEvent(RoutingEvent.WAYPOINT_DRAGGED,
        {
          detail: {
            waypointId: this.#currentDraggedWaypoint.getId(),
            newCoordinate: dragEvent.coordinate
          }
        }));

    return true;
  }

  _onPointerEnterWaypoint (feature) {
    this.#currentHoverWaypoint = feature;
    this.#map.getTargetElement().classList.remove('is-cursor-pointer');
    this.#map.getTargetElement().classList.add('is-cursor-pointer');
    this.#routingEventBus.dispatchEvent(new CustomEvent(RoutingEvent.POINTER_ENTER_WAYPOINT, { detail: feature }));
  }

  _onPointerExitWaypoint (feature) {
    this.#currentHoverWaypoint = null;
    this.#map.getTargetElement().classList.remove('is-cursor-pointer');
    this.#routingEventBus.dispatchEvent(new CustomEvent(RoutingEvent.POINTER_EXIT_WAYPOINT, { detail: feature }));
  }

  _onClickOnWaypoint (feature) {
    this.#routingEventBus.dispatchEvent(new CustomEvent(RoutingEvent.CLICK_ON_WAYPOINT, { detail: feature }));
  }

  _onClickOnMap (coordinate) {
    this.#routingEventBus.dispatchEvent(new CustomEvent(RoutingEvent.CLICK_ON_MAP, { detail: coordinate }));
  }

}

// Two different interaction classes are required in order to distinguish between them for the primary map interaction.
// There are 2 different buttons in the map toolbar, so each of them should have its own unique map interaction.
export class DirectionsRoutingInteraction extends RoutingInteraction {

}

export class IsochronesRoutingInteraction extends RoutingInteraction {

}
